/**
 * The ten squares of the ranked grid, and which card sits in each.
 *
 * The grid is always ten cells, five across and two down, because an empty
 * square is a (+) tile rather than a missing tile - a person should be able to
 * see how much room is left without counting. So this turns a list of wish
 * rows into exactly ten positions, filled or empty.
 *
 * The slot a card holds comes from its own `position`, which the database hands
 * out, and never from where the row happened to land in an array. Two devices
 * adding at the same moment get their slots from one place, and a list read in
 * added_at order still renders in rank order.
 */

import { TOP_TEN_LIMIT } from '../../services/wishList';

/**
 * @param {Array} items wish_list rows, is_priority already true
 * @param {number} limit how many squares the grid has
 * @returns {Array<{ position: number, card: Object|null }>} always `limit` long
 */
export function buildTopTenSlots(items, limit = TOP_TEN_LIMIT) {
  const slots = Array.from({ length: limit }, (_, i) => ({ position: i + 1, card: null }));
  const rows = (items || []).filter(Boolean);

  // A row whose position the database has not given out yet, or gave out twice
  // through some history the app cannot see, still has to render somewhere. It
  // waits here and takes the lowest free square, so a card is never silently
  // dropped off a grid its owner knows it is on.
  const unplaced = [];

  for (const row of rows) {
    const pos = Number(row.position);
    const index = Number.isInteger(pos) && pos >= 1 && pos <= limit ? pos - 1 : -1;
    if (index >= 0 && !slots[index].card) slots[index].card = row;
    else unplaced.push(row);
  }

  // Oldest first, which is the same tie-break the migration used when it
  // numbered the unordered list for the first time.
  unplaced.sort((a, b) => new Date(a.added_at || 0) - new Date(b.added_at || 0));
  for (const row of unplaced) {
    const free = slots.find((s) => !s.card);
    if (!free) break;
    free.card = row;
  }

  return slots;
}

/** How many of the squares are taken. */
export function countFilledSlots(items, limit = TOP_TEN_LIMIT) {
  return buildTopTenSlots(items, limit).filter((s) => s.card).length;
}
